import type { components } from '@pba/types-openapi-client';
import type { BadgeNotificationsSettingsDataObject, UserSettingsDataObject } from 'models/service';

export type BadgeNotificationsSettings = {
  enableAppIcon?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
  enableNotifications?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
};

export const createBadgeNotificationsSettings = (
  settingsData: BadgeNotificationsSettingsDataObject,
): BadgeNotificationsSettings => ({
  enableAppIcon: settingsData.enable_app_icon
    ? {
        changeAllowed: settingsData.enable_app_icon.change_allowed,
        value: settingsData.enable_app_icon.value,
      }
    : undefined,
  enableNotifications: settingsData.enable_notifications
    ? {
        changeAllowed: settingsData.enable_notifications.change_allowed,

        value: settingsData.enable_notifications.value,
      }
    : undefined,
});

export type EmailNotificationsSettings = {
  dailyDigestEvening?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
  dailyDigestMorning?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
  donations?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
  enableNotifications?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
  events?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
  giving?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
  knowledgeCenter?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
  messaging?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
  newsfeed?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
  program?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
};

export type PushNotificationsSettings = {
  donations?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
  enableNotifications?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
  events?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
  giving?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
  knowledgeCenter?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
  messaging?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
  newsfeed?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
  program?: {
    changeAllowed?: boolean;
    value?: boolean;
  };
};

export const createPushNotificationsSettings = (
  // TODO: remove after open api fix
  settingsData?: {
    giving?: {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- TODO: Needs to be fixed later
      change_allowed?: boolean;
      value?: boolean;
    };
  } & components['schemas']['UserDetail']['push_notifications_settings'],
): PushNotificationsSettings => ({
  donations: settingsData?.donations
    ? {
        changeAllowed: settingsData.donations.change_allowed,
        value: settingsData.donations.value,
      }
    : undefined,
  enableNotifications: settingsData?.enable_notifications
    ? {
        changeAllowed: settingsData.enable_notifications.change_allowed,
        value: settingsData.enable_notifications.value,
      }
    : undefined,
  events: settingsData?.events
    ? {
        changeAllowed: settingsData.events.change_allowed,
        value: settingsData.events.value,
      }
    : undefined,
  giving: settingsData?.giving
    ? {
        changeAllowed: settingsData.giving.change_allowed,
        value: settingsData.giving.value,
      }
    : undefined,
  knowledgeCenter: settingsData?.knowledge_center
    ? {
        changeAllowed: settingsData.knowledge_center.change_allowed,
        value: settingsData.knowledge_center.value,
      }
    : undefined,
  messaging: settingsData?.messaging
    ? {
        changeAllowed: settingsData.messaging.change_allowed,
        value: settingsData.messaging.value,
      }
    : undefined,
  newsfeed: settingsData?.newsfeed
    ? {
        changeAllowed: settingsData.newsfeed.change_allowed,
        value: settingsData.newsfeed.value,
      }
    : undefined,
  program: settingsData?.program
    ? {
        changeAllowed: settingsData.program.change_allowed,
        value: settingsData.program.value,
      }
    : undefined,
});

export const createEmailNotificationsSettings = (
  settingsData: {
    giving?: {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- TODO: Needs to be fixed later
      change_allowed: boolean;
      value: boolean;
    };
  } & components['schemas']['UserDetail']['email_notifications_settings'],
): EmailNotificationsSettings => ({
  dailyDigestEvening: settingsData.daily_digest_evening
    ? {
        changeAllowed: settingsData.daily_digest_evening.change_allowed,
        value: settingsData.daily_digest_evening.value,
      }
    : undefined,
  dailyDigestMorning: settingsData.daily_digest_morning
    ? {
        changeAllowed: settingsData.daily_digest_morning.change_allowed,
        value: settingsData.daily_digest_morning.value,
      }
    : undefined,
  donations: settingsData.donations
    ? {
        changeAllowed: settingsData.donations.change_allowed,
        value: settingsData.donations.value,
      }
    : undefined,
  enableNotifications: settingsData.enable_notifications
    ? {
        changeAllowed: settingsData.enable_notifications.change_allowed,
        value: settingsData.enable_notifications.value,
      }
    : undefined,
  events: settingsData.events
    ? {
        changeAllowed: settingsData.events.change_allowed,
        value: settingsData.events.value,
      }
    : undefined,
  giving: settingsData.giving
    ? {
        changeAllowed: settingsData.giving.change_allowed,
        value: settingsData.giving.value,
      }
    : undefined,
  knowledgeCenter: settingsData.knowledge_center
    ? {
        changeAllowed: settingsData.knowledge_center.change_allowed,
        value: settingsData.knowledge_center.value,
      }
    : undefined,
  messaging: settingsData.messaging
    ? {
        changeAllowed: settingsData.messaging.change_allowed,
        value: settingsData.messaging.value,
      }
    : undefined,
  newsfeed: settingsData.newsfeed
    ? {
        changeAllowed: settingsData.newsfeed.change_allowed,
        value: settingsData.newsfeed.value,
      }
    : undefined,
  program: settingsData.program
    ? {
        changeAllowed: settingsData.program.change_allowed,
        value: settingsData.program.value,
      }
    : undefined,
});

export type UserSettings = {
  translations: {
    enableTranslations: {
      value: boolean;
    };
    language: {
      value: string;
    };
  };
};

export const createUserSettings = (settingsData: UserSettingsDataObject): UserSettings => {
  const translations = settingsData.translations;

  return {
    translations: {
      enableTranslations: { value: translations.enable_translations.value },
      language: { value: translations.language.value },
    },
  };
};
